// Function index:
//   ChatMount  — the controller interface returned by mountChat.
//   mountChat(initial, selfUserId?) — wire up #log + #input form + #presence
//                                     + #typing; returns ChatMount.
//   setChatAutoScroll(on) / setChatTimestamps(on) — options-gear prefs,
//       persisted in localStorage and applied to subsequent log lines.
//   ChatMount.say(text) — programmatic send+echo of a player line (the combat
//       window's free-text row); returns whether the frame left the machine.
//   ChatMount.location(text) — arrival/location narration line (uses 'gm' styling).
//   ChatMount.playerSaid(userId, name, text) — another member's table-talk
//       line; skips userId === selfUserId (the local echo already rendered it).
//   ChatMount.history(entries) — replace the log with the rehydrated
//       narrative slice (first paint + reconnect resync).
//
// selfUserId is the logged-in user's id; supplied so we can render presence
// chips for *other* party members only and not show "you're typing" to
// yourself. Optional — when omitted, presence shows everyone.

import type { WS } from './ws.js';

// Display prefs (controlled by the in-game options gear). Module-scoped so
// the live ChatMount picks them up on the next appended line.
let autoScroll = localStorage.getItem('rz.chat.autoscroll') !== '0';
let showTimestamps = localStorage.getItem('rz.chat.timestamps') === '1';

export function setChatAutoScroll(on: boolean): void {
  autoScroll = on;
  localStorage.setItem('rz.chat.autoscroll', on ? '1' : '0');
}
export function setChatTimestamps(on: boolean): void {
  showTimestamps = on;
  localStorage.setItem('rz.chat.timestamps', on ? '1' : '0');
}
const hhmm = (): string => new Date().toTimeString().slice(0, 5);

export interface PresenceUser {
  user_id: number;
  display_name: string;
  typing: boolean;
}

// One rehydrated line of the narrative log (bootstrap `messages` slice).
export interface ChatHistoryEntry {
  role: 'player' | 'gm' | 'system';
  self: boolean;
  name: string | null;
  content: string;
}

export interface ChatMount {
  playerSaid(userId: number, name: string, text: string): void;
  history(entries: ChatHistoryEntry[]): void;
  system(text: string): void;
  dice(text: string): void;
  hp(text: string, defeated?: boolean): void;
  gmThinking(): void; // show a pulsing "GM is composing…" placeholder pre-first-token
  gmAppend(chunk: string): void; // streaming-friendly: appends to the last GM bubble
  gmEnd(): void; // marks the current GM bubble as complete; next chunk starts fresh
  location(text: string): void; // an arrival/location narration line
  attachWS(ws: WS): void; // call after the ws is open so the input form can send
  // Programmatic say — the combat window's free-text row routes through this
  // so send + local echo behave exactly like the chat input. Returns whether
  // the frame actually left the machine (false = keep the text for retry).
  say(text: string): boolean;
  setInputEnabled(enabled: boolean, placeholder?: string): void;
  setPresence(users: PresenceUser[]): void;
}

const TYPING_IDLE_MS = 3000;

export function mountChat(initial: WS | null, selfUserId: number | null = null): ChatMount {
  const log = document.getElementById('log') as HTMLDivElement;
  const form = document.getElementById('input') as HTMLFormElement;
  const input = document.getElementById('say') as HTMLInputElement;
  const presenceEl = document.getElementById('presence') as HTMLDivElement;
  const typingEl = document.getElementById('typing') as HTMLDivElement;
  const defaultPlaceholder = input.placeholder;

  let activeGmLine: HTMLDivElement | null = null;
  let thinkingLine: HTMLDivElement | null = null;
  let ws: WS | null = initial;

  // Typing-send state: track whether we've told the server we're typing, so
  // we don't spam the same event on every keystroke. Stop is debounced.
  let sentTyping = false;
  let typingIdleTimer: ReturnType<typeof setTimeout> | null = null;

  const sendTyping = (typing: boolean) => {
    if (!ws) return;
    if (sentTyping === typing) return;
    sentTyping = typing;
    ws.send('player.typing', { typing });
  };

  // Each line holds an optional timestamp span + a `.body` span carrying the
  // text. gm streaming appends into `.body` so the timestamp survives.
  const append = (cls: string, text: string): HTMLDivElement => {
    const line = document.createElement('div');
    line.className = cls;
    if (showTimestamps) {
      const ts = document.createElement('span');
      ts.className = 'ts';
      ts.textContent = hhmm();
      line.appendChild(ts);
    }
    const body = document.createElement('span');
    body.className = 'body';
    body.textContent = text;
    line.appendChild(body);
    log.appendChild(line);
    if (autoScroll) log.scrollTop = log.scrollHeight;
    return line;
  };

  input.addEventListener('input', () => {
    if (input.disabled) return;
    if (input.value.length === 0) {
      if (typingIdleTimer) { clearTimeout(typingIdleTimer); typingIdleTimer = null; }
      sendTyping(false);
      return;
    }
    sendTyping(true);
    if (typingIdleTimer) clearTimeout(typingIdleTimer);
    typingIdleTimer = setTimeout(() => { sendTyping(false); typingIdleTimer = null; }, TYPING_IDLE_MS);
  });

  input.addEventListener('blur', () => {
    if (typingIdleTimer) { clearTimeout(typingIdleTimer); typingIdleTimer = null; }
    sendTyping(false);
  });

  // One send path for the chat input AND the combat window's free-text row.
  // Echo only what actually left the machine: a send on a dead socket
  // keeps the caller's text (retry is one Enter away) and says so, instead
  // of rendering a line the GM never received.
  const say = (raw: string): boolean => {
    const text = raw.trim();
    if (!text) return false;
    if (!ws) { append('system', '· not connected yet'); return false; }
    if (!ws.send('player.say', { content: text })) {
      append('system', '· connection lost — your message was not sent; it will stay in the box until the line is back');
      return false;
    }
    append('player', `> ${text}`);
    activeGmLine = null;
    return true;
  };

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (input.disabled) return;
    if (!say(input.value)) return;
    // Sending implies "done typing" — clear local state so the next
    // keystroke fires a fresh typing:true.
    if (typingIdleTimer) { clearTimeout(typingIdleTimer); typingIdleTimer = null; }
    sentTyping = false;
    input.value = '';
  });

  const renderPresence = (users: PresenceUser[]) => {
    presenceEl.innerHTML = '';
    typingEl.textContent = '';

    // Presence chips: show every connected user (incl. self), self last so
    // others read left-to-right. Empty when only self is online.
    const others = users.filter((u) => u.user_id !== selfUserId);
    const self = users.find((u) => u.user_id === selfUserId);
    const ordered = self ? [...others, self] : others;
    if (ordered.length === 0) return;
    for (const u of ordered) {
      const chip = document.createElement('span');
      chip.className = `chip${u.typing && u.user_id !== selfUserId ? ' typing' : ''}`;
      const dot = document.createElement('span'); dot.className = 'dot';
      const label = document.createElement('span');
      label.textContent = u.user_id === selfUserId ? `${u.display_name} (you)` : u.display_name;
      chip.appendChild(dot);
      chip.appendChild(label);
      presenceEl.appendChild(chip);
    }

    // Typing line: list other users currently typing, ignore self.
    const typers = others.filter((u) => u.typing).map((u) => u.display_name);
    if (typers.length === 1) typingEl.textContent = `${typers[0]} is typing…`;
    else if (typers.length === 2) typingEl.textContent = `${typers[0]} and ${typers[1]} are typing…`;
    else if (typers.length > 2) typingEl.textContent = `${typers.length} people are typing…`;
  };

  return {
    say,
    playerSaid: (userId, name, text) => {
      // The sender's own line was already echoed locally on submit — render
      // only other members' broadcast copies.
      if (selfUserId !== null && userId === selfUserId) return;
      append('player', `${name} > ${text}`);
    },
    history: (entries) => {
      // Replace, don't append: the same call serves first paint AND the
      // reconnect resync, where the log already holds a partial copy.
      log.replaceChildren();
      activeGmLine = null;
      thinkingLine = null;
      for (const e of entries) {
        if (e.role === 'gm') append('gm', e.content);
        else if (e.role === 'system') append('system', `· ${e.content}`);
        else if (e.self) append('player', `> ${e.content}`);
        else append('player', `${e.name ?? 'A player'} > ${e.content}`);
      }
    },
    system: (text) => append('system', `· ${text}`),
    dice: (text) => append('dice', `▸ ${text}`),
    hp: (text, defeated = false) => {
      const line = append('hp', text);
      if (defeated) {
        const span = document.createElement('span');
        span.className = 'down';
        span.textContent = ' — down';
        line.appendChild(span);
      }
    },
    gmThinking: () => {
      // One placeholder per turn; ignore if a bubble is already streaming.
      if (thinkingLine || activeGmLine) return;
      thinkingLine = append('gm thinking', 'The GM is composing…');
    },
    gmAppend: (chunk) => {
      // First token: reuse the thinking bubble in place so it morphs into the
      // narration rather than leaving a stray "composing…" line above it.
      if (thinkingLine && !activeGmLine) {
        thinkingLine.classList.remove('thinking');
        const tb = thinkingLine.querySelector('.body') ?? thinkingLine;
        tb.textContent = '';
        activeGmLine = thinkingLine;
        thinkingLine = null;
      }
      if (!activeGmLine) activeGmLine = append('gm', '');
      const body = activeGmLine.querySelector('.body') ?? activeGmLine;
      body.textContent += chunk;
      if (autoScroll) log.scrollTop = log.scrollHeight;
    },
    gmEnd: () => {
      // Turn ended with no text (failure / empty output): drop the dangling
      // placeholder so it doesn't pulse forever.
      if (thinkingLine && !activeGmLine) { thinkingLine.remove(); thinkingLine = null; }
      activeGmLine = null;
    },
    location: (text) => { append('gm', text); },
    attachWS: (newWs) => { ws = newWs; },
    setInputEnabled: (enabled, placeholder) => {
      input.disabled = !enabled;
      input.placeholder = placeholder ?? defaultPlaceholder;
    },
    setPresence: renderPresence,
  };
}
